import { ColumnDef } from '@tanstack/react-table'
import { Dispatch, SetStateAction } from 'react'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'

import { ShipmentNotification } from '@/interfaces/shipmentNotification'
import ShipmentStatusBadge from '@/components/misc/badges/ShipmentStatusBadge'

// Función para formatear fecha con mes en letras
const formatDateWithMonth = (dateString: string): string => {
  if (!dateString) return ''
  
  // Manejar fechas ISO con zona horaria (ej: 2025-07-31T00:00:00.000Z)
  if (dateString.includes('T')) {
    // Extraer solo la parte de la fecha (YYYY-MM-DD) antes de la T
    const dateOnly = dateString.split('T')[0]
    const dateParts = dateOnly.split('-')
    if (dateParts.length === 3) {
      const [year, month, day] = dateParts.map(Number)
      if (year && month && day) {
        const months = [
          'ene', 'feb', 'mar', 'abr', 'may', 'jun',
          'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
        ]
        
        return `${day} ${months[month - 1]} ${year}`
      }
    }
  }
  
  // Manejar fechas en formato YYYY-MM-DD
  const dateParts = dateString.split('-')
  if (dateParts.length === 3) {
    const [year, month, day] = dateParts.map(Number)
    if (year && month && day) {
      const months = [
        'ene', 'feb', 'mar', 'abr', 'may', 'jun',
        'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
      ]
      
      return `${day} ${months[month - 1]} ${year}`
    }
  }
  
  // Fallback: usar Date si el formato no es reconocido
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    
    const months = [
      'ene', 'feb', 'mar', 'abr', 'may', 'jun',
      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
    ]
    
    const day = date.getDate()
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    
    return `${day} ${month} ${year}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

export const getColumns = (
  setShowEditNotification: Dispatch<SetStateAction<boolean>>,
  setNotificationToEdit: Dispatch<SetStateAction<ShipmentNotification | undefined>>,
  setShowDeleteNotification: Dispatch<SetStateAction<boolean>>,
  setNotificationToDelete: Dispatch<SetStateAction<number | undefined>>
) => {
  const columns: ColumnDef<ShipmentNotification>[] = [
    {
      accessorKey: 'shouldArrivedAt',
      header: 'Fecha de arribo',
      cell: ({ row }) => {
        const date = row.original?.shouldArrivedAt || ''
        const formattedDate = formatDateWithMonth(date)
        return <span>{formattedDate}</span>
      },
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: 'Nombre',
      cell: ({ row }) => {
        const name = row.original?.name || ''
        return <span>{name.trim()}</span>
      },
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const statusValue = row.original?.status || ''
        return <ShipmentStatusBadge status={statusValue} />
      },
      enableHiding: false,
    },
    {
      accessorKey: 'providerCompanyName',
      header: 'Proveedor',
      cell: ({ row }) => {
        const providerName = row.original?.providerCompanyName || 'Sin asignar'
        return <span>{providerName}</span>
      },
      enableHiding: true,
    },
    {
      accessorKey: 'managerCompanyName',
      header: 'Operador Logístico',
      cell: ({ row }) => {
        const managerName = row.original?.managerCompanyName || 'Sin asignar'
        return <span>{managerName}</span>
      },
      enableHiding: true,
    },
    {
      accessorKey: 'createdByName',
      header: 'Creado por',
      cell: ({ row }) => {
        const createdBy = row.original?.createdByName || 'Sin asignar'
        return <span>{createdBy}</span>
      },
      enableHiding: true,
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const notification = row.original

        return (
          <div className={'flex justify-start'}>
            <div role={'button'} className={`p-2`} onClick={() => {}}>
              <PencilSquareIcon
                className={`h-5 w-5 text-blue-700`}
                onClick={() => {
                  setNotificationToEdit(notification)
                  setShowEditNotification(true)
                }}
              />
            </div>

            <div role={'button'} className={`p-2`} onClick={() => {}}>
              <TrashIcon
                className={`h-5 w-5 text-red-700`}
                onClick={() => {
                  setNotificationToDelete(notification.id)
                  setShowDeleteNotification(true)
                }}
              />
            </div>
          </div>
        )
      },
      enableHiding: false, // Important column, don't allow hiding
    },
  ]

  return columns
}
